import type { SimpleChanges } from '@angular/core';
import { Component, Input, Optional } from '@angular/core';

import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import type { User } from '@core/account-types';
import { trackByProp } from '@core/common-legacy';
import type { ApprovalStepWithDecisions } from '@core/requisitions-types';
import { AssignedFlowsActions } from '@recruitee/requisitions-data';

import { RequisitionsFlowStepComponent } from '../../../components/requisitions-flow-step/requisitions-flow-step.component';
import { ApprovalFlowFacade } from '../../../facades/single-requisition/approval-flow.facade';
import { EditApproversFacade } from '../../../facades/single-requisition/edit-approvers.facade';
import { FlowStepViewContext } from '../../../requisitions.types';

@Component({
  selector: 'rt-requisitions-flow-steps-list-container',
  template: `
    @for (step of steps; track trackById($index, step)) {
      <rt-requisitions-flow-step
        [flowStep]="step"
        [users]="users"
        [isEditable]="editableSteps[step.id!]"
        [canSendReminders]="canSendReminders"
        [context]="context"
        (edit)="editApprovers(step)"
        (sendReminder)="sendReminder($event)"
        (removeApprover)="removeApprover($event, step)"
      ></rt-requisitions-flow-step>
    }
  `,
  styleUrls: ['./requisitions-flow-steps-list-container.component.less'],
  imports: [RequisitionsFlowStepComponent],
  providers: [EditApproversFacade],
})
export class RequisitionsFlowStepsListContainerComponent {
  @Input() public requisitionId: number;
  @Input() public steps: ApprovalStepWithDecisions[] = [];
  @Input() public users: Record<number, User>;
  @Input() public context: FlowStepViewContext = FlowStepViewContext.Page;
  @Input() public isReadOnly: boolean;

  public editableSteps: Record<number, boolean> = {};
  public canSendReminders: boolean;

  public trackById = trackByProp<{ id?: number }>({ prop: 'id' });

  constructor(
    private store: Store,
    private editApproversFacade: EditApproversFacade,
    @Optional()
    private approvalFlowFacade: ApprovalFlowFacade,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (this.users && this.context === FlowStepViewContext.Page) {
      this.setEditableSteps();
      this.canSendReminders = this.approvalFlowFacade?.canSendReminders;
    }
  }

  public editApprovers(step: ApprovalStepWithDecisions) {
    this.editApproversFacade
      .selectApprovers$(step)
      ?.pipe(take(1))
      .subscribe(newApproverIds => {
        if (!newApproverIds || !newApproverIds.length) return;
        this.store.dispatch(
          AssignedFlowsActions.updateApprovers({
            payload: {
              requisitionId: this.requisitionId,
              stepId: step.id!,
              approverIds: newApproverIds,
            },
          }),
        );
      });
  }

  public removeApprover(id: number, step: ApprovalStepWithDecisions) {
    this.store.dispatch(
      AssignedFlowsActions.deleteApprover({
        payload: {
          requisitionId: this.requisitionId,
          stepId: step.id!,
          approverId: id,
        },
      }),
    );
  }

  public sendReminder(approverDecisionId: number) {
    this.store.dispatch(
      AssignedFlowsActions.sendReminder({
        payload: { requisitionId: this.requisitionId, approverDecisionId },
      }),
    );
  }

  private setEditableSteps() {
    this.editableSteps = {};
    this.steps.forEach(step => {
      this.editableSteps[step.id!] =
        this.editApproversFacade.isStepEditable(step) && !this.isReadOnly;
    });
  }
}
